import { useEffect, useState } from 'react'

/**
 * VISTA PREVIA de la tarjeta de invitación en el panel (Martin, 24 sep 2026:
 * "no es claro qué es cada campo"). Dibuja la tarjeta como la ve el cliente
 * en ligier.com.ar/invitacion/… — mismo orden, mismos colores (negro, blanco,
 * verde de Ligier; foto de la experiencia en B&N) — con los datos que arma LGR
 * (`vista_previa`), y resalta en verde lo que cambia el campo que se está
 * editando (`foco`). Es una réplica a escala: la pieza real vive en la tienda.
 */

const VERDE = '#2d6a3f'
const VERDE_VIVO = '#36b36a'
const SAGE = '#9cc4ac'
const MONO = '"Space Mono", ui-monospace, Menlo, monospace'
const SANS = '-apple-system, BlinkMacSystemFont, "Helvetica Neue", Helvetica, Arial, sans-serif'
const SERIF = '"Cormorant Garamond", Georgia, serif'

// Las dos tipografías de la tienda que LGR no usa, solo para esta réplica.
function useFuentesTienda() {
  useEffect(() => {
    const id = 'fuentes-tienda-invitacion'
    if (document.getElementById(id)) return
    const link = document.createElement('link')
    link.id = id
    link.rel = 'stylesheet'
    link.href = 'https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@1,500&family=Space+Mono&display=swap'
    document.head.appendChild(link)
  }, [])
}

const resalte = (activo) => (activo ? { outline: `2px solid ${VERDE_VIVO}`, outlineOffset: 4, borderRadius: 2 } : {})

function restan(momento) {
  if (!momento) return null
  const s = Math.floor((new Date(momento).getTime() - Date.now()) / 1000)
  if (s <= 0) return null
  const d = Math.floor(s / 86400)
  return d > 0
    ? [[d, d === 1 ? 'día' : 'días'], [Math.floor((s % 86400) / 3600), 'horas'], [Math.floor((s % 3600) / 60), 'min']]
    : [[Math.floor(s / 3600), 'horas'], [Math.floor((s % 3600) / 60), 'min'], [s % 60, 'seg']]
}

export default function VistaPreviaInvitacion({ tarjeta, foco }) {
  useFuentesTienda()
  const [cuenta, setCuenta] = useState(() => restan(tarjeta?.momento))
  useEffect(() => {
    setCuenta(restan(tarjeta?.momento))
    const id = setInterval(() => setCuenta(restan(tarjeta?.momento)), 30000)
    return () => clearInterval(id)
  }, [tarjeta?.momento])

  if (!tarjeta) return null
  const etiqueta = { fontFamily: MONO, fontSize: 8.5, letterSpacing: '0.2em', textTransform: 'uppercase', color: SAGE }

  return (
    <div style={{ position: 'relative', overflow: 'hidden', borderRadius: 14, background: '#000', padding: '18px 12px' }}>
      {tarjeta.imagen && (
        <img src={tarjeta.imagen} alt="" style={{
          position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover',
          filter: tarjeta.imagenColor ? 'none' : 'grayscale(1)', opacity: 0.3,
        }} />
      )}
      <div style={{
        position: 'relative', border: '1px solid rgba(255,255,255,0.15)', background: 'rgba(0,0,0,0.86)',
        borderRadius: 2, padding: '20px 16px 18px', color: '#fff', textTransform: 'none',
      }}>
        <p style={{ ...etiqueta, margin: 0 }}>Para Laura</p>

        <p style={{ ...etiqueta, color: VERDE_VIVO, letterSpacing: '0.28em', margin: '16px 0 0', ...resalte(foco === 'encabezado') }}>
          {tarjeta.encabezado || ' '}
        </p>
        <h4 style={{ margin: '6px 0 0', fontFamily: SANS, fontWeight: 600, fontSize: 24, lineHeight: 1.05, textWrap: 'balance' }}>
          {tarjeta.titulo}
        </h4>
        {tarjeta.frase && (
          <p style={{ margin: '8px 0 0', fontFamily: SERIF, fontStyle: 'italic', fontSize: 15, lineHeight: 1.25, color: 'rgba(255,255,255,0.8)', ...resalte(foco === 'frase') }}>
            {tarjeta.frase}
          </p>
        )}

        <div style={{ height: 1, margin: '16px 0 14px', background: `linear-gradient(90deg, transparent, ${VERDE} 18%, ${VERDE} 82%, transparent)` }} />

        <div style={{ display: 'grid', gap: 9 }}>
          {tarjeta.filas.map((f) => {
            const esDonde = /^(d[oó]nde|lugar)$/i.test(f.etiqueta)
            const esParking = /^parking$/i.test(f.etiqueta)
            return (
              <div key={f.etiqueta} style={{ display: 'grid', gridTemplateColumns: '74px 1fr', gap: 8, alignItems: 'baseline', ...resalte(esParking && foco === 'parking') }}>
                <span style={etiqueta}>{f.etiqueta}</span>
                <span style={{ fontFamily: SANS, fontSize: 12, lineHeight: 1.3 }}>
                  {f.valor}
                  {f.detalle && (
                    <span style={{
                      display: 'block', marginTop: 1, fontSize: 11, color: 'rgba(255,255,255,0.65)',
                      textDecoration: 'underline', textDecorationColor: 'rgba(54,179,106,0.6)', textUnderlineOffset: 3,
                      ...resalte(esDonde && foco === 'direccion'),
                    }}>{f.detalle}</span>
                  )}
                </span>
              </div>
            )
          })}
          <div style={{ display: 'grid', gridTemplateColumns: '74px 1fr', gap: 8, alignItems: 'baseline' }}>
            <span style={etiqueta}>Tu reserva</span>
            <span style={{ fontFamily: MONO, fontSize: 11.5, color: VERDE_VIVO }}>2 lugares</span>
          </div>
        </div>

        {cuenta && (
          <div style={{ marginTop: 18, textAlign: 'center' }}>
            <p style={{ ...etiqueta, margin: 0, ...resalte(foco === 'cuenta_etiqueta') }}>{tarjeta.momentoEtiqueta || ' '}</p>
            <div style={{ display: 'flex', justifyContent: 'center', gap: 18, marginTop: 6 }}>
              {cuenta.map(([n, u]) => (
                <div key={u} style={{ display: 'grid' }}>
                  <span style={{ fontFamily: MONO, fontSize: 22, lineHeight: 1 }}>{String(n).padStart(2, '0')}</span>
                  <span style={{ ...etiqueta, fontSize: 7.5, color: 'rgba(255,255,255,0.5)', marginTop: 4 }}>{u}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div style={{ marginTop: 18, minHeight: 34, display: 'grid', placeItems: 'center', background: VERDE, borderRadius: 2, fontFamily: SANS, fontSize: 12, fontWeight: 500 }}>
          Confirmo asistencia
        </div>

        {tarjeta.nota && (
          <p style={{ margin: '12px 0 0', textAlign: 'center', fontFamily: SERIF, fontStyle: 'italic', fontSize: 13.5, color: 'rgba(255,255,255,0.7)', ...resalte(foco === 'nota') }}>
            {tarjeta.nota}
          </p>
        )}
        <p style={{ margin: '12px 0 0', textAlign: 'center', fontFamily: MONO, fontSize: 9, color: 'rgba(255,255,255,0.7)', letterSpacing: '0.1em' }}>
          Agendar&nbsp;&nbsp;&nbsp;@{tarjeta.instagram}
        </p>
      </div>
    </div>
  )
}
